import React from 'react';
import { TrendingUp, DollarSign, Percent, AlertCircle } from 'lucide-react';
import { OverviewLineChart } from '../reports/OverviewLineChart';
import { OverviewMetricCard } from '../reports/OverviewMetricCard';

export interface ProfitAnalysisProps {
  profit: {
    grossProfit: number;
    grossMargin: number;
    netProfit: number;
    netMargin: number;
    costOfGoodsSold: number;
    trends: {
      grossProfit: { current: number; previous: number; change: number; changePercent: number };
      grossMargin: { current: number; previous: number; change: number; changePercent: number };
    };
    profitTrend?: Array<{ date: string; value: number }>;
  };
  revenueTrend?: Array<{ date: string; value: number }>;
  loading?: boolean;
}

export const ProfitAnalysis: React.FC<ProfitAnalysisProps> = ({
  profit,
  revenueTrend = [],
  loading = false,
}) => {
  const formatCurrency = (value: number) => {
    return `€${value.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const trendData = profit.profitTrend && profit.profitTrend.length > 0
    ? profit.profitTrend
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
          <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Profit Analysis
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Gross profit metrics with period-over-period trends
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <OverviewMetricCard
          title="Gross Profit"
          value={formatCurrency(profit.grossProfit)}
          subtitle={`Margin: ${formatPercentage(profit.grossMargin)}`}
          icon={DollarSign}
          trend={{
            value: Math.abs(profit.trends.grossProfit.changePercent),
            positive: profit.trends.grossProfit.change >= 0,
          }}
          gradient="from-emerald-500 to-green-600"
          format="currency"
          loading={loading}
        />

        <OverviewMetricCard
          title="Gross Margin"
          value={formatPercentage(profit.grossMargin)}
          subtitle={`${formatCurrency(profit.grossProfit)} profit`}
          icon={Percent}
          trend={{
            value: Math.abs(profit.trends.grossMargin.changePercent),
            positive: profit.trends.grossMargin.change >= 0,
          }}
          gradient="from-blue-500 to-cyan-600"
          format="percentage"
          loading={loading}
        />

        <OverviewMetricCard
          title="Cost of Goods Sold"
          value={formatCurrency(profit.costOfGoodsSold)}
          subtitle="Total product costs"
          icon={AlertCircle}
          gradient="from-amber-500 to-orange-600"
          format="currency"
          loading={loading}
        />
      </div>

      {trendData && trendData.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Daily Profit Trend
          </h4>
          <OverviewLineChart
            data={trendData.map(d => ({
              date: d.date,
              value: d.value,
              label: `${d.date}: ${formatCurrency(d.value)}`,
            }))}
            title=""
            color="green"
            height={250}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Profit Breakdown
          </h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-400">Revenue</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(profit.grossProfit + profit.costOfGoodsSold)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-400">Cost of Goods Sold</span>
              <span className="font-semibold text-red-600 dark:text-red-400">
                -{formatCurrency(profit.costOfGoodsSold)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border-t-2 border-green-500">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Gross Profit</span>
              <span className="font-bold text-green-600 dark:text-green-400">
                {formatCurrency(profit.grossProfit)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Margin Analysis
          </h4>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Gross Margin</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {formatPercentage(profit.grossMargin)}
                </span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(profit.grossMargin, 100)}%` }}
                />
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Period Change</span>
                <span
                  className={`text-sm font-semibold flex items-center gap-1 ${
                    profit.trends.grossProfit.change >= 0
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  <TrendingUp className={`h-3.5 w-3.5 ${profit.trends.grossProfit.change < 0 ? 'rotate-180' : ''}`} />
                  {formatCurrency(Math.abs(profit.trends.grossProfit.change))}
                </span>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Profitability Status</span>
                <span
                  className={`text-sm font-semibold ${
                    profit.grossMargin > 40
                      ? 'text-green-600 dark:text-green-400'
                      : profit.grossMargin > 20
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {profit.grossMargin > 40 ? 'Excellent' : profit.grossMargin > 20 ? 'Good' : 'Needs Improvement'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
